import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-enterprise';
import { AgGridReact, type CustomStatusPanelProps } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  agGridContainerStyles,
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { colors } from '../theme/colors';
import { customerReportService } from '../services/customerReportService';
import { IsolatedReportPreview } from './IsolatedReportPreview';
import { IsolatedReportPreviewAll } from './IsolatedReportPreviewAll';

const StatusBarRightComponent: React.FC<CustomStatusPanelProps> = (props) => {
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);

  useEffect(() => {
    const updateCounts = () => {
      const total = props.api.getDisplayedRowCount();
      const selectedRows = props.api.getSelectedRows();
      setTotalCount(total);
      setSelectedCount(selectedRows.length);
    };
    props.api.addEventListener('rowDataUpdated', updateCounts);
    props.api.addEventListener('selectionChanged', updateCounts);
    updateCounts();
    return () => {
      props.api.removeEventListener('rowDataUpdated', updateCounts);
      props.api.removeEventListener('selectionChanged', updateCounts);
    };
  }, [props.api]);

  return (
    <div className="ag-status-name-value" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span>Selected: {selectedCount} out of {totalCount} Record(s)</span>
    </div>
  );
};

interface IsolatedReportTableProps {
  projectNumber: string;
  projectName: string;
  month: string;
  selectedOrder?: string;
  customOrderNumber?: string;
  showPreview?: boolean;
  onBackFromPreview?: () => void;
  onSelectionChange?: (selectedCount: number) => void;
}

export const IsolatedReportTable: React.FC<IsolatedReportTableProps> = ({
  projectNumber,
  projectName,
  month,
  selectedOrder = '',
  customOrderNumber = '',
  showPreview: showPreviewFromParent = false,
  onBackFromPreview,
  onSelectionChange,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [showSinglePreview, setShowSinglePreview] = useState<boolean>(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set());
  const hasInitialSelectionRef = useRef(false);
  const prevShowPreviewRef = useRef(showPreviewFromParent);

  const {
    data: employeesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer-report-isolated-employees', projectNumber, month, selectedOrder],
    queryFn: () => customerReportService.getIsolatedReportEmployees(projectNumber, month, selectedOrder),
    enabled: !!projectNumber && !!month,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const employees = useMemo(() => employeesData?.employees ?? [], [employeesData]);
  const selectedEmployees = useMemo(
    () => employees.filter((emp) => selectedEmployeeIds.has(emp.employee_id)),
    [employees, selectedEmployeeIds]
  );

  useEffect(() => {
    if (employees.length > 0) {
      setSelectedEmployeeIds(new Set(employees.map((e) => e.employee_id)));
      hasInitialSelectionRef.current = false;
      onSelectionChange?.(employees.length);
    }
  }, [employees, onSelectionChange]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    if (gridApi && selectedEmployeeIds.size > 0 && !hasInitialSelectionRef.current) {
      gridApi.forEachNode((node) => {
        const id = node.data?.employee_id;
        node.setSelected(!!id && selectedEmployeeIds.has(id), false);
      });
      hasInitialSelectionRef.current = true;
    }
  }, [gridApi, selectedEmployeeIds]);

  const handleSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api || gridApi;
    if (api) {
      const selected = api.getSelectedRows();
      const ids = new Set(selected.map((r: { employee_id: string }) => r.employee_id));
      setSelectedEmployeeIds(ids);
      onSelectionChange?.(ids.size);
    }
  }, [gridApi, onSelectionChange]);

  useEffect(() => {
    const wasInPreview = prevShowPreviewRef.current;
    const isReturningFromPreview = wasInPreview && !showPreviewFromParent;
    prevShowPreviewRef.current = showPreviewFromParent;
    if (isReturningFromPreview && selectedEmployeeIds.size > 0) {
      hasInitialSelectionRef.current = false;
      const restore = () => {
        const api = gridRef.current?.api;
        if (api) {
          api.forEachNode((node) => {
            const id = node.data?.employee_id;
            node.setSelected(!!id && selectedEmployeeIds.has(id), false);
          });
          hasInitialSelectionRef.current = true;
        } else {
          setTimeout(restore, 100);
        }
      };
      const t = setTimeout(restore, 150);
      return () => clearTimeout(t);
    }
  }, [showPreviewFromParent, selectedEmployeeIds]);

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Employee Name',
      field: 'employee_name',
      maxWidth: 300,
      minWidth: 200,
      headerClass: `${agGridCSSClasses.modernHeader} filterable`,
      cellClass: agGridCSSClasses.modernCell,
      sortable: true,
      filter: true,
      suppressHeaderMenuButton: false,
      menuTabs: ['filterMenuTab'],
    },
    {
      headerName: '',
      field: 'preview',
      flex: 1,
      minWidth: 100,
      headerClass: agGridCSSClasses.modernHeader,
      cellClass: agGridCSSClasses.modernCell,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => (
        <button
          onClick={() => {
            setSelectedEmployeeId(params.data?.employee_id);
            setShowSinglePreview(true);
          }}
          style={{
            padding: '0.375rem 0.75rem',
            backgroundColor: colors.accent.blue,
            color: colors.neutral.white,
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Preview
        </button>
      ),
    },
  ], []);

  const rowSelection = useMemo(
    () => ({ mode: 'multiRow' as const, headerCheckbox: true, checkboxes: true }),
    []
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        { statusPanel: StatusBarRightComponent, key: 'statusBarRight', align: 'right' },
      ],
    }),
    []
  );

  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onSelectionChanged: handleSelectionChanged,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    getRowId: (params: any) => params.data?.employee_id,
  };

  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: colors.accent.red + '10',
          color: colors.accent.red,
          borderRadius: '6px',
        }}
      >
        Error loading employees: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  const showGrid = !showPreviewFromParent && !showSinglePreview;

  return (
    <div style={{ height: 'calc(100dvh - 410px)', display: 'flex', flexDirection: 'column' }}>
      {showGrid && (
        <>
          <style>{agGridThemeStyles}</style>
          <style>{`
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button {
              display: flex !important;
              opacity: 1 !important;
              visibility: visible !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button .ag-icon:not(.ag-icon-filter),
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button .ag-icon:not(.ag-icon-filter) {
              display: none !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button .ag-icon-filter,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button .ag-icon-filter,
            .ag-theme-quartz .ag-header-cell.filterable .ag-icon-filter {
              display: block !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-filter-button,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-filter-button {
              display: block !important;
            }
          `}</style>
          <div
            className="ag-theme-quartz"
            style={{
              ...agGridContainerStyles,
              flex: '1 1 auto',
              minHeight: '400px',
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={employees}
              columnDefs={columnDefs}
              defaultColDef={agGridDefaultColDef}
              gridOptions={gridOptions}
              rowSelection={rowSelection}
              statusBar={statusBar}
              suppressRowClickSelection={false}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
            />

            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: colors.neutral.grey[600],
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: colors.neutral.white,
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #e2e8f0',
                    borderTop: '2px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Loading employees...
              </div>
            )}
          </div>
        </>
      )}

      {showSinglePreview && selectedEmployeeId && (
        <IsolatedReportPreview
          projectNumber={projectNumber}
          projectName={projectName}
          month={month}
          employeeId={selectedEmployeeId}
          employeeName={employees.find((e) => e.employee_id === selectedEmployeeId)?.employee_name ?? ''}
          selectedOrder={selectedOrder}
          customOrderNumber={customOrderNumber}
          onBack={() => {
            setShowSinglePreview(false);
            setSelectedEmployeeId(null);
          }}
        />
      )}

      {showPreviewFromParent && selectedEmployees.length > 0 && (
        <IsolatedReportPreviewAll
          employees={selectedEmployees.map((e) => ({ employeeId: e.employee_id, employeeName: e.employee_name }))}
          projectNumber={projectNumber}
          projectName={projectName}
          month={month}
          selectedOrder={selectedOrder}
          customOrderNumber={customOrderNumber}
          onBack={onBackFromPreview ?? (() => {})}
        />
      )}
    </div>
  );
};
